import { z } from 'zod'
import { ProviderId, USAGE_KINDS, UsageKind } from '../enums'
import { Instant } from './primitives'

export const ANALYTICS_DEFAULT_DAYS = 30
export const ANALYTICS_MAX_DAYS = 365
export const USAGE_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

export const UsageQuantity = z.number().int().nonnegative()
export const UsageDay = z.iso.date()
export const UsageMonth = z.string().regex(USAGE_MONTH_PATTERN)

export const UsageByKind = z.record(UsageKind, UsageQuantity)
export type UsageByKind = z.infer<typeof UsageByKind>

export const emptyUsageByKind = (): UsageByKind =>
  Object.fromEntries(USAGE_KINDS.map((kind) => [kind, 0])) as UsageByKind

export const UsageProviderTokens = z.object({ provider: ProviderId.nullable(), tokens: UsageQuantity })
export type UsageProviderTokens = z.infer<typeof UsageProviderTokens>

export const UsageBudget = z.object({
  monthlyTokenBudget: UsageQuantity,
  tokensUsed: UsageQuantity,
  tokensRemaining: UsageQuantity,
  exhausted: z.boolean(),
})
export type UsageBudget = z.infer<typeof UsageBudget>

export const UsageSummary = z.object({
  periodStart: UsageDay,
  periodEnd: UsageDay,
  budget: UsageBudget,
  byKind: UsageByKind,
  byProvider: z.array(UsageProviderTokens),
  updatedAt: Instant.nullable(),
})
export type UsageSummary = z.infer<typeof UsageSummary>

export const UsageQuery = z.strictObject({ month: UsageMonth.optional() })
export type UsageQuery = z.infer<typeof UsageQuery>

export const AnalyticsDay = z.object({ day: UsageDay, byKind: UsageByKind })
export type AnalyticsDay = z.infer<typeof AnalyticsDay>

export const AnalyticsSummary = z.object({
  from: UsageDay,
  to: UsageDay,
  totals: UsageByKind,
  byProvider: z.array(UsageProviderTokens),
  daily: z.array(AnalyticsDay),
})
export type AnalyticsSummary = z.infer<typeof AnalyticsSummary>

export const AnalyticsSummaryQuery = z.strictObject({
  days: z.coerce.number().int().min(1).max(ANALYTICS_MAX_DAYS).default(ANALYTICS_DEFAULT_DAYS),
})
export type AnalyticsSummaryQuery = z.infer<typeof AnalyticsSummaryQuery>

export const AI_PAUSE_REASONS = ['credentials_missing', 'budget_exhausted'] as const
export const AiPauseReason = z.enum(AI_PAUSE_REASONS)
export type AiPauseReason = z.infer<typeof AiPauseReason>

export const AiStatus = z.strictObject({ aiPaused: z.boolean(), reason: AiPauseReason.nullable() })
export type AiStatus = z.infer<typeof AiStatus>
